// glide-ui: 5
//
// Select — Radix-backed select on desktop; native <select> on mobile so users
// get the OS picker (iOS wheel, Android sheet) instead of a custom popup.
// parts: Select, SelectTrigger (size?: "sm"|"default"), SelectValue, SelectContent, SelectViewport (internal), SelectItem, SelectGroup, SelectLabel, SelectSeparator, SelectScrollUpButton, SelectScrollDownButton
// props (Select): value, onValueChange, defaultValue, disabled, name
// <Select value={v} onValueChange={setV}><SelectTrigger><SelectValue placeholder="Pick one" /></SelectTrigger><SelectContent><SelectItem value="a">A</SelectItem></SelectContent></Select>

import React, { forwardRef, useEffect, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@glide/cn";

// Select picks Radix vs native based on *input method*, not viewport
// width: touch devices get the OS-native picker (iOS wheel, Android
// sheet) at any screen size, mouse/trackpad devices get Radix's
// keyboard-friendly popover. This is intentionally different from the
// other overlay morphs (Dialog/Popover/DropdownMenu) which morph on
// width — those are about layout fitting the screen.
//
// `(any-hover: hover) and (any-pointer: fine)` matches when at least
// one connected input can hover AND at least one is fine-grained:
// hybrid laptops with both a touchscreen and a trackpad still match
// (and correctly get Radix). Pure-touch phones / tablets without a
// stylus or trackpad do not match.
const FINE_POINTER_QUERY = "(any-hover: hover) and (any-pointer: fine)";

function useCanHover(): boolean {
  const [canHover, setCanHover] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(FINE_POINTER_QUERY).matches
      : true,
  );
  useEffect(() => {
    const mq = window.matchMedia(FINE_POINTER_QUERY);
    const onChange = () => setCanHover(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return canHover;
}

export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: "sm" | "default";
  }
>(({ className, children, size = "default", ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-slot="select-trigger"
    data-size={size}
    className={cn(
      "cursor-pointer flex w-full items-center justify-between rounded-md border border-transparent bg-inset",
      "px-3 py-2 text-sm",
      "data-[size=default]:h-9 data-[size=sm]:h-8",
      "[&>span[data-placeholder]]:text-tertiary",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "aria-invalid:border-error aria-invalid:ring-error/20",
      "[&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectScrollUpButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    data-slot="select-scroll-up-button"
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

export const SelectScrollDownButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    data-slot="select-scroll-down-button"
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border",
        "bg-raised text-primary shadow-md",
        "data-[state=open]:animate-popover-in data-[state=closed]:animate-popover-out",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectLabel = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    data-slot="select-label"
    className={cn("px-2 py-1.5 text-xs font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

// Radix throws at render if a <Select.Item> has value="" (it reserves the
// empty string for "no selection / show placeholder"), which unmounts the
// whole app. Agent-generated apps routinely emit value="" for a clear/"All"
// option or via a mapped value that coerced to "". We swap "" for a single
// space (a non-empty string Radix tolerates) on the item only, and decode
// the sentinel back to "" in the root's onValueChange so the app still
// observes "". The root's value/defaultValue pass through untouched: Radix
// needs a literal "" there to keep showing the placeholder for Selects with
// no empty-valued item — encoding it to " " would make Radix think a
// selection exists, match no item, and render a blank trigger.
const EMPTY_VALUE = " "; // one space — deliberately non-empty
function encodeValue<T extends string | undefined>(v: T): T {
  return (v === "" ? EMPTY_VALUE : v) as T;
}
function decodeValue(v: string): string {
  return v === EMPTY_VALUE ? "" : v;
}

export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm",
      "py-1.5 pl-8 pr-2 text-sm outline-none",
      "focus:bg-inset focus:text-primary",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
    value={encodeValue(props.value)}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export const SelectSeparator = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-slot="select-separator"
    className={cn("-mx-1 my-1 h-px bg-border", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// ─── Responsive root ──────────────────────────────────────────────────
//
// Desktop: Radix Select (current behavior).
// Mobile: a real <select> that fires the OS picker on tap. We walk the
// children just enough to find the SelectValue's placeholder, every
// SelectItem's value + label text, and the SelectTrigger's size — then
// emit a single flat native element. SelectGroup / SelectLabel /
// optgroup aren't honored (uncommon in agent code); items inside a
// group still extract correctly and render flat. Non-text content
// inside a SelectItem (icons, badges) is stripped on mobile — <option>
// only carries text. For richer pickers, use a Dialog with a list.

type SelectRootProps = React.ComponentProps<typeof SelectPrimitive.Root>;

function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

type NativeTriggerProps = Partial<
  Pick<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    | "id"
    | "className"
    | "aria-label"
    | "aria-labelledby"
    | "aria-describedby"
    | "aria-invalid"
    | "aria-required"
    | "title"
    | "tabIndex"
    | "autoFocus"
  >
>;

function parseSelectChildren(node: React.ReactNode) {
  let placeholder: string | undefined;
  let size: "sm" | "default" = "default";
  let triggerProps: NativeTriggerProps = {};
  const items: { value: string; label: string; disabled?: boolean }[] = [];

  function visit(n: React.ReactNode) {
    React.Children.forEach(n, (child) => {
      if (!React.isValidElement(child)) return;
      const props = child.props as {
        size?: "sm" | "default";
        placeholder?: string;
        value?: string;
        disabled?: boolean;
        children?: React.ReactNode;
      };
      if (child.type === SelectTrigger) {
        if (props.size) size = props.size;
        const trigger = child.props as NativeTriggerProps;
        triggerProps = {
          id: trigger.id,
          className: trigger.className,
          "aria-label": trigger["aria-label"],
          "aria-labelledby": trigger["aria-labelledby"],
          "aria-describedby": trigger["aria-describedby"],
          "aria-invalid": trigger["aria-invalid"],
          "aria-required": trigger["aria-required"],
          title: trigger.title,
          tabIndex: trigger.tabIndex,
          autoFocus: trigger.autoFocus,
        };
      } else if (child.type === SelectValue) {
        if (typeof props.placeholder === "string")
          placeholder = props.placeholder;
        return;
      } else if (child.type === SelectItem) {
        items.push({
          value: props.value ?? "",
          label: textOf(props.children),
          disabled: props.disabled,
        });
        return;
      }
      if (props.children !== undefined) visit(props.children);
    });
  }
  visit(node);
  return { placeholder, size, triggerProps, items };
}

function MobileNativeSelect({
  value,
  defaultValue,
  onValueChange,
  disabled,
  name,
  required,
  children,
}: SelectRootProps) {
  const { placeholder, size, triggerProps, items } =
    parseSelectChildren(children);
  const isControlled = value !== undefined;
  // Uncontrolled path: mirror the native <select>'s value in local state
  // so React re-renders track what the user picked. Without this,
  // `defaultValue` would be frozen at the initial render.
  const [internal, setInternal] = useState<string>(defaultValue ?? "");
  const v = isControlled ? (value ?? "") : internal;

  return (
    <div className={cn("relative w-full min-w-0", triggerProps.className)}>
      <select
        id={triggerProps.id}
        aria-label={triggerProps["aria-label"]}
        aria-labelledby={triggerProps["aria-labelledby"]}
        aria-describedby={triggerProps["aria-describedby"]}
        aria-invalid={triggerProps["aria-invalid"]}
        aria-required={triggerProps["aria-required"]}
        title={triggerProps.title}
        tabIndex={triggerProps.tabIndex}
        autoFocus={triggerProps.autoFocus}
        value={v}
        onChange={(e) => {
          if (!isControlled) setInternal(e.target.value);
          onValueChange?.(e.target.value);
        }}
        disabled={disabled}
        name={name}
        required={required}
        data-slot="select-trigger"
        data-size={size}
        className={cn(
          // Match Radix trigger geometry so layouts don't shift between
          // breakpoints. `appearance-none` strips the browser default
          // caret so our chevron is the only one shown.
          "block w-full appearance-none rounded-md border border-transparent bg-inset",
          "px-3 pr-9 py-2 text-sm leading-normal",
          size === "default" ? "h-11 min-h-11" : "h-10 min-h-10",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "aria-invalid:border-error aria-invalid:ring-error/20",
        )}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {items.map((i) => (
          <option key={i.value} value={i.value} disabled={i.disabled}>
            {i.label}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50"
      />
    </div>
  );
}

export function Select(props: SelectRootProps) {
  const canHover = useCanHover();
  if (canHover) {
    // Leave value/defaultValue as-is (see EMPTY_VALUE note) — only decode
    // onValueChange, since a picked empty-valued item arrives as the " "
    // sentinel and the app expects "".
    return (
      <SelectPrimitive.Root
        {...props}
        onValueChange={
          props.onValueChange
            ? (v) => props.onValueChange!(decodeValue(v))
            : undefined
        }
      />
    );
  }
  return <MobileNativeSelect {...props} />;
}
